import { useEffect, useState } from "react";
import { Eye, MessageSquare, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { STATUS_COLORS, formatDateShort, formatDate } from "@/lib/helpers";

const STATUSES = ["Diterima", "Diverifikasi", "Diproses", "Ditindaklanjuti", "Selesai"];
const REGENCIES = ["Semua", "Kota Kendari", "Kabupaten Konawe", "Kabupaten Kolaka", "Kabupaten Muna", "Kabupaten Buton", "Kabupaten Konawe Selatan", "Kabupaten Bombana", "Kabupaten Wakatobi"];
const TYPES = ["Semua", "Saran", "Kritik", "Aspirasi", "Pertanyaan", "Masukan pelayanan"];

export default function AspirationsAdmin() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ status: "Semua", regency: "Semua", type: "Semua" });
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState({});

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v !== "Semua" && p.set(k, v));
    api.get(`/aspirations?${p}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filters]);

  const openDetail = (a) => { setActive(a); setEdit({ status: a.status, pic: a.pic, response: a.response, internal_notes: a.internal_notes }); };

  const save = async (closeTicket = false) => {
    const payload = { ...edit };
    if (closeTicket) payload.status = "Selesai";
    await api.put(`/aspirations/${active.id}`, payload);
    toast.success(closeTicket ? "Tiket ditutup (Selesai)" : "Aspirasi diperbarui");
    setActive(null); load();
  };

  return (
    <div>
      <AdminHeader title="Aspirasi Masyarakat" description="Kelola dan tindak lanjuti aspirasi masyarakat." />

      <div className="flex flex-wrap gap-3 mb-5">
        {[["status", STATUSES.concat().length ? ["Semua", ...STATUSES] : []], ["regency", REGENCIES], ["type", TYPES]].map(([key, opts]) => (
          <Select key={key} value={filters[key]} onValueChange={(v) => setFilters({ ...filters, [key]: v })}>
            <SelectTrigger className="w-full sm:w-52" data-testid={`filter-${key}`}><SelectValue /></SelectTrigger>
            <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o === "Semua" ? `Semua ${key === "status" ? "Status" : key === "regency" ? "Wilayah" : "Jenis"}` : o}</SelectItem>)}</SelectContent>
          </Select>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="aspirasi-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Nama</TableHead>
              <TableHead>Kab/Kota</TableHead><TableHead>Jenis</TableHead><TableHead>Subjek</TableHead>
              <TableHead>Status</TableHead><TableHead>PIC</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`asp-row-${a.id}`}>
                <TableCell className="font-mono text-xs font-bold text-navy">{a.ticket}</TableCell>
                <TableCell className="text-sm text-slate-500">{formatDateShort(a.created_at)}</TableCell>
                <TableCell className="text-sm font-semibold text-navy">{a.name}</TableCell>
                <TableCell className="text-sm text-slate-500">{a.regency}</TableCell>
                <TableCell className="text-sm text-slate-500">{a.type}</TableCell>
                <TableCell className="text-sm text-slate-500 max-w-[180px]"><span className="line-clamp-1">{a.subject}</span></TableCell>
                <TableCell><span className={`text-xs font-bold px-2 py-1 rounded-full border ${STATUS_COLORS[a.status]}`}>{a.status}</span></TableCell>
                <TableCell className="text-sm text-slate-500">{a.pic}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(a)} data-testid={`view-asp-${a.id}`}><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Tidak ada aspirasi.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="aspirasi-action-drawer">
          {active && (
            <>
              <SheetHeader><SheetTitle className="font-mono text-brandred">{active.ticket}</SheetTitle><SheetDescription>Detail dan tindak lanjut aspirasi masyarakat.</SheetDescription></SheetHeader>
              <div className="mt-5 space-y-4">
                <div className="bg-surface rounded-xl p-4 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-slate-400 text-xs">Nama</span><div className="font-semibold text-navy">{active.name}</div></div>
                    <div><span className="text-slate-400 text-xs">Wilayah</span><div className="font-semibold text-navy">{active.regency}</div></div>
                    <div><span className="text-slate-400 text-xs">Email</span><div className="font-semibold text-navy break-all">{active.email}</div></div>
                    <div><span className="text-slate-400 text-xs">Telepon</span><div className="font-semibold text-navy">{active.phone || "-"}</div></div>
                    <div><span className="text-slate-400 text-xs">Jenis</span><div className="font-semibold text-navy">{active.type}</div></div>
                    <div><span className="text-slate-400 text-xs">Tanggal</span><div className="font-semibold text-navy">{formatDate(active.created_at)}</div></div>
                  </div>
                </div>
                <div><span className="text-slate-400 text-xs">Subjek</span><div className="font-semibold text-navy">{active.subject}</div></div>
                <div><span className="text-slate-400 text-xs">Isi Aspirasi</span><p className="text-sm text-slate-700 bg-white border border-slate-200 rounded-lg p-3 mt-1">{active.message}</p></div>
                {active.attachment_path && (
                  <div>
                    <span className="text-slate-400 text-xs">Lampiran</span>
                    <a href={`${API}/files/${active.attachment_path}?auth=${localStorage.getItem("harmoni_token")}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-sm font-semibold text-brandred hover:underline mt-1" data-testid="asp-attachment-download">
                      <FileDown className="h-4 w-4" /> {active.attachment || "Unduh lampiran"}
                    </a>
                  </div>
                )}

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <div>
                    <Label>Ubah Status</Label>
                    <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="asp-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>PIC (Penanggung Jawab)</Label><Input value={edit.pic} onChange={(e) => setEdit({ ...edit, pic: e.target.value })} className="mt-1.5" data-testid="asp-pic" /></div>
                  <div><Label>Respons untuk Masyarakat</Label><Textarea value={edit.response} onChange={(e) => setEdit({ ...edit, response: e.target.value })} rows={3} className="mt-1.5" data-testid="asp-response" /></div>
                  <div><Label>Catatan Internal</Label><Textarea value={edit.internal_notes} onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })} rows={2} className="mt-1.5" data-testid="asp-notes" /></div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={() => save(false)} className="flex-1 bg-navy text-white" data-testid="save-asp"><MessageSquare className="h-4 w-4 mr-1.5" /> Simpan</Button>
                  <Button onClick={() => save(true)} variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="close-asp">Tutup Tiket</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
